import { CheckCircleIcon } from '@chakra-ui/icons';
import {
  Card,
  CardBody,
  CardHeader,
  Flex,
  HStack,
  Heading,
  Icon,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { ArchiveIcon } from '@heroicons/react/outline';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  IdentityType,
  IdentityVerificationStatus,
  useGetCompanyCaptableQuery,
  useGetOptionAssetsQuery,
} from '@blockpulse3/graphql/hooks';
import { formatNumberPercentage } from '@blockpulse3/helpers';
import {
  AssetBadge,
  ErrorQueryCard,
  IdentityAvatar,
  TableContainer,
} from '@blockpulse3/ui/commons';

type Props = unknown;

/**
 * ShareholderTable.
 *
 * @returns {JSX.Element}
 */
export function ShareholderTable(): JSX.Element {
  const i18nShareholderTable = useTranslations('ShareholderTable');

  const { companyId = '' } = useParams();

  const captableReq = useGetCompanyCaptableQuery({ variables: { companyId } });
  const optionAssetReq = useGetOptionAssetsQuery({ variables: { companyId } });

  if (captableReq.loading || optionAssetReq.loading) {
    return (
      <Skeleton>
        <Card h="270px" />
      </Skeleton>
    );
  }

  if (captableReq.error || optionAssetReq.loading) {
    return <ErrorQueryCard />;
  }

  const shareholders = captableReq.data?.company?.capTable?.shareholders || [];
  const optionAssets = optionAssetReq?.data?.getOptionAssets || [];
  const fullyDilutedTotalSupply = captableReq.data?.company.capTable?.fullyDilutedTotalSupply || 1;

  return (
    <Card>
      <CardHeader>
        <Heading size="md">{i18nShareholderTable('Title')}</Heading>
      </CardHeader>
      <CardBody>
        <TableContainer maxHeight="none">
          <Table variant="striped">
            <Thead>
              <Tr>
                <Th>{i18nShareholderTable('Name')}</Th>
                <Th>{i18nShareholderTable('SecuritiesHeld')}</Th>
                <Th isNumeric>{i18nShareholderTable('VotingRights')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {shareholders.map((shareholder, index) => {
                const isCompany = shareholder.identity?.type === IdentityType.COMPANY;
                return (
                  <Tr key={`${index}-${shareholder.identity.identifier}`}>
                    <Td>
                      <HStack spacing="2.5">
                        <IdentityAvatar identity={shareholder.identity} />
                        <Stack spacing="1">
                          <HStack spacing="1">
                            <Text fontWeight="600">{shareholder.identity?.name}</Text>
                            {shareholder.verificationStatus ===
                              IdentityVerificationStatus.APPROVED && (
                              <Icon as={CheckCircleIcon} boxSize="15px" color="green.400" />
                            )}
                            {isCompany && (
                              <Text fontWeight="500">({shareholder.identity?.identifier})</Text>
                            )}
                          </HStack>
                          <Text color="gray.500" fontSize="sm" fontWeight="400">
                            {shareholder.identity?.email}
                          </Text>
                        </Stack>
                      </HStack>
                    </Td>
                    <Td>
                      <AssetBadge type={shareholder.assetType} value={shareholder.shareCount} />
                    </Td>
                    <Td isNumeric fontWeight="600">
                      <Text>
                        {formatNumberPercentage(shareholder.capitalHeld)} (
                        {formatNumberPercentage(shareholder.fullyDilutedCapitalHeld)})
                      </Text>
                    </Td>
                  </Tr>
                );
              })}
              {optionAssets.map((pool) => {
                const percentage =
                  (pool.conversionParity * pool.remainingSupply * 100) / fullyDilutedTotalSupply;

                return (
                  <Tr key={pool.code}>
                    <Td>
                      <HStack spacing="2">
                        <Flex
                          alignItems="center"
                          bg="gray.100"
                          boxSize="9"
                          flexShrink="0"
                          justifyContent="center"
                          rounded="sm"
                        >
                          <Icon as={ArchiveIcon} boxSize="50%" />
                        </Flex>
                        <Text fontWeight="600">{pool.name}</Text>
                      </HStack>
                    </Td>
                    <Td>
                      <AssetBadge
                        grayed={true}
                        type={pool.assetType}
                        value={pool.remainingSupply}
                      />
                    </Td>
                    <Td isNumeric fontWeight="600">
                      <Text>({formatNumberPercentage(percentage)})</Text>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  );
}

export type ShareholderTableProps = Props;
